/* Auto-scrolling category row for the B139 "Who This Is For" section.
 *
 * The row is a native horizontal scroller in the markup. This file only adds
 * drift on top of it, so with JavaScript off, with reduced motion requested, or
 * with the speed set to zero, the row stays a perfectly usable scroller rather
 * than a frozen strip showing card one.
 *
 * The loop is seamless because the card list is cloned once and the scroll
 * position wraps at the halfway mark. The clones are aria-hidden and their
 * focusable descendants, if any are ever added, are taken out of the tab order,
 * so the duplicate never reaches assistive technology or the keyboard.
 */

(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CategoryMarquee {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-b139-marquee-viewport]');
      this.track = root.querySelector('[data-b139-marquee-track]');
      if (!this.viewport || !this.track) return;

      this.speed = parseFloat(root.dataset.speed || '0');
      this.paused = true;
      this.cloned = false;
      this.lastFrame = 0;
      this.carry = 0;
      this.frame = null;

      this.tick = this.tick.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
      this.sync = this.sync.bind(this);

      this.bind();
      this.sync();
    }

    bind() {
      const prev = this.root.querySelector('[data-b139-marquee-prev]');
      const next = this.root.querySelector('[data-b139-marquee-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.root.addEventListener('pointerenter', this.pause);
      this.root.addEventListener('pointerleave', this.resume);
      this.root.addEventListener('focusin', this.pause);
      this.root.addEventListener('focusout', this.resume);

      // A visitor who takes hold of the row by hand keeps it until they let go.
      this.viewport.addEventListener('pointerdown', this.pause);
      this.viewport.addEventListener('touchstart', this.pause, { passive: true });
      this.viewport.addEventListener('touchend', this.resume);
      this.viewport.addEventListener('scroll', () => this.wrap(), { passive: true });

      document.addEventListener('visibilitychange', this.sync);
      REDUCED_MOTION.addEventListener('change', this.sync);

      // Drifting a row nobody is looking at burns battery for nothing.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        });
        this.observer.observe(this.root);
      } else {
        this.visible = true;
      }
    }

    get canAnimate() {
      return (
        this.speed > 0 &&
        !REDUCED_MOTION.matches &&
        !document.hidden &&
        this.visible !== false &&
        this.track.scrollWidth > this.viewport.clientWidth + 1
      );
    }

    cloneOnce() {
      if (this.cloned) return;
      const originals = Array.from(this.track.children);
      if (!originals.length) return;
      originals.forEach((card) => {
        const copy = card.cloneNode(true);
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a, button, input, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.track.appendChild(copy);
      });
      this.originalCount = originals.length;
      this.cloned = true;
    }

    /* Distance from a card to its own clone, which is one full cycle of the
       list. Measured rather than halving the track's scroll width, because the
       track carries the page gutter as padding and that padding is not part of
       the cycle. */
    get cycle() {
      const first = this.track.children[0];
      const firstClone = this.track.children[this.originalCount];
      if (!first || !firstClone) return 0;
      return firstClone.offsetLeft - first.offsetLeft;
    }

    sync() {
      if (this.canAnimate) {
        this.cloneOnce();
        this.resume();
      } else {
        this.pause();
      }
    }

    pause() {
      this.paused = true;
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    resume() {
      if (!this.canAnimate || this.root.matches(':hover, :focus-within')) return;
      if (this.frame) return;
      this.paused = false;
      this.lastFrame = 0;
      this.carry = 0;
      this.frame = requestAnimationFrame(this.tick);
    }

    tick(now) {
      if (this.paused) return;
      if (this.lastFrame) {
        const elapsed = Math.min(now - this.lastFrame, 100) / 1000;
        // scrollLeft snaps to whole pixels, so at this speed a per-frame step
        // of well under 1px would be rounded away and the row would never
        // move. The remainder is carried until it is worth a pixel.
        this.carry += this.speed * elapsed;
        const step = Math.floor(this.carry);
        if (step >= 1) {
          this.carry -= step;
          this.viewport.scrollLeft += step;
          this.wrap();
        }
      }
      this.lastFrame = now;
      this.frame = requestAnimationFrame(this.tick);
    }

    wrap() {
      if (!this.cloned) return;
      const half = this.cycle;
      if (half <= 0) return;
      if (this.viewport.scrollLeft > 1) this.hasMoved = true;
      if (this.viewport.scrollLeft >= half) {
        this.viewport.scrollLeft -= half;
      } else if (this.hasMoved && this.viewport.scrollLeft <= 0) {
        // Only loop backwards once the row has actually been somewhere, so the
        // resting position at page load is card one rather than a jump to the
        // middle of the list.
        this.viewport.scrollLeft += half;
      }
    }

    step(direction) {
      const card = this.track.querySelector('.b139-audience__card');
      const distance = card ? card.getBoundingClientRect().width + 24 : this.viewport.clientWidth * 0.8;
      this.viewport.scrollBy({
        left: distance * direction,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-b139-marquee]').forEach((root) => {
      if (root.dataset.b139MarqueeReady) return;
      root.dataset.b139MarqueeReady = 'true';
      new CategoryMarquee(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
